/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  SearchChemicalElements,
  SearchChemistryGroups,
  SearchProductGroups,
  SearchSteelGrades,
} from "contexts/search/context";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import {
  ChefGroup,
  ChemicalConstraint,
  FormattedBasketConstraint,
  MaterialLimit,
  ProductGroup,
  SteelGrade,
} from "src/store/api/generatedApi";
import { partition } from "helpers";
import { chemicalExpressionFromConstraintCoefficients } from "./chemicalExpressionFromConstraintCoefficients";
import React from "react";
import { formatMaterialLimits } from "components/common/chips/materialLimit";
import { isMaterialLimitNullified } from "src/utils/materialLimitOverrides";
import { formatConstraint } from "components/common/chemicalConstraints";

export interface FormattedProductLimit {
  id: number; // For data grid purposes

  chemistryGroup: string;
  productGroup: ProductGroup | null;
  steelGrades: SteelGrade[];

  maximumFailureRate: string;
  chemicalTolerances: Record<string, string>;

  materialLimits: Record<string, string>;

  matchesFilter: boolean;
}

export const useProductConstraintRows = (
  chemistryGroups: SearchChemistryGroups,
  productGroups: SearchProductGroups | null,
  chemicalElements: SearchChemicalElements,
  steelGrades: SearchSteelGrades,
  materialLimits: MaterialLimit[],
  filterSteelGrades: string[]
): FormattedProductLimit[] => {
  const formatChemistryGroup = useFormatChemistryGroup(
    productGroups,
    chemicalElements,
    steelGrades,
    materialLimits
  );

  const constraints = chemistryGroups.byIndex.map(formatChemistryGroup);

  const [matching, nonMatching] = partition(
    (constraint) =>
      filterSteelGrades.some((grade) =>
        constraint.steelGrades
          .map((steelGrade) => steelGrade.name)
          .includes(grade)
      ),
    constraints
  );

  return [
    ...matching.map((constraint) => ({ ...constraint, matchesFilter: true })),
    ...nonMatching,
  ];
};

const useFormatChemistryGroup = (
  productGroups: SearchProductGroups | null,
  chemicalElements: SearchChemicalElements,
  steelGrades: SearchSteelGrades,
  materialLimits: MaterialLimit[]
) => {
  const { format } = useNumberSerialiser();
  const units = useUnitsFormatter(false);

  const formatMixMaterialLimit = useFormatMaterialLimit();
  const formatChemicalConstraint =
    useFormatChemicalConstraint(chemicalElements);

  return (chemistryGroup: ChefGroup) => ({
    matchesFilter: false,
    id: chemistryGroup.id!,
    chemistryGroup: chemistryGroup.name,
    productGroup:
      (productGroups?.byIndex ?? []).find((group) =>
        group.steel_grades
          .map(({ name }) => name)
          .includes(chemistryGroup.steel_grades[0]?.name ?? "")
      ) ?? null,
    steelGrades: chemistryGroup.steel_grades.map(
      ({ id }) => steelGrades.byId[id]!
    ),

    maximumFailureRate:
      format(chemistryGroup.max_failure_rate) + units("yield"),
    materialLimits: Object.fromEntries(
      materialLimits
        .filter(({ chef_group_id }) => chemistryGroup.id === chef_group_id)
        .filter((materialLimit) => !isMaterialLimitNullified(materialLimit))
        .map((materialLimit) => formatMixMaterialLimit(materialLimit))
    ),
    chemicalTolerances: Object.fromEntries(
      chemistryGroup.chemical_constraints
        .filter(
          (constraint) =>
            constraint.weight_percent_max < 100 ||
            (constraint.weight_percent_min ?? 0) > 0
        )
        .map(formatChemicalConstraint)
    ),
  });
};

const useFormatMaterialLimit = () => {
  const units = useUnitsFormatter(false);
  const { t } = useTenantTranslation();

  // TODO: the numbers need to be localised here
  const doFormat = React.useCallback(
    (materialLimit: MaterialLimit): [string, string] => {
      const mixBounds = materialLimit.bounds.find((b) => b.basket_id == null);
      return [
        t(materialLimit.name ?? ""),
        formatMaterialLimits(
          {
            minMass: mixBounds?.min_mass,
            maxMass: mixBounds?.max_mass,
          },
          units
        ),
      ];
    },
    [units, t]
  );
  return doFormat;
};

const useFormatChemicalConstraint = (
  chemicalElements: SearchChemicalElements
) => {
  const { format } = useNumberSerialiser({ decimalPlaces: 3 });
  const units = useUnitsFormatter(false);

  return (constraint: ChemicalConstraint): [string, string] => {
    return [
      chemicalExpressionFromConstraintCoefficients(
        constraint.name,
        constraint.coefficients,
        chemicalElements.byId
      ),
      formatConstraint(
        chemicalExpressionFromConstraintCoefficients(
          constraint.name,
          constraint.coefficients,
          chemicalElements.byId
        ),
        constraint.weight_percent_min,
        constraint.weight_percent_max,
        format,
        units("yield")
      ),
    ];
  };
};

export const getBasketConstraintRows = (
  constraints: FormattedBasketConstraint[]
): (FormattedBasketConstraint & { id: string })[] =>
  constraints.map((constraint) => ({
    ...constraint,
    id: constraint.basket_number.toString(),
  }));
